import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Clock, Tag, ArrowRight, Menu, ChevronDown } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO, { SEO_PRESETS } from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { API_ENDPOINTS } from '../config/api';
import { colors, typography, spacing, breakpoints } from '../../styles/theme';

const ArticleListPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadArticles();
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const mobile = windowWidth < parseInt(breakpoints.mobile);

  async function loadArticles() {
    try {
      setLoading(true);
      const response = await api.get(API_ENDPOINTS.CONTENT.ARTICLES);
      setArticles(response.articles || []);
    } catch (err) {
      console.error('Failed to load articles:', err);
      setError('Failed to load articles. Please try again later.');
    } finally {
      setLoading(false);
    }
  }

  const categories = ['all', ...new Set(articles.map(a => a.category).filter(Boolean))];
  const filteredArticles = selectedCategory === 'all' 
    ? articles 
    : articles.filter(a => a.category === selectedCategory);
  
  const featuredArticles = articles.filter(a => a.featured);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      color: colors.textPrimary,
      fontFamily: '"DM Sans", system-ui, sans-serif'
    }}>
      <SEO {...SEO_PRESETS.articles} />
      
      {/* Public Navigation */}
      <nav style={{
        padding: mobile ? '1rem 1rem' : '1.5rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '1200px',
        margin: '0 auto',
        gap: mobile ? '0.75rem' : '0',
      }}>
        <div 
          onClick={() => navigate('/')}
          style={{
            fontSize: mobile ? '1.25rem' : '1.5rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            cursor: 'pointer'
          }}>
          IQ Reps
        </div>
        <div style={{ 
          display: 'flex', 
          gap: mobile ? '0.5rem' : '1rem', 
          alignItems: 'center',
          flex: 1,
          justifyContent: 'flex-end'
        }}>
          {!mobile && (
            <>
              <button
                onClick={() => navigate('/articles')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: colors.primary,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  padding: '0.5rem 1rem',
                  fontWeight: '600'
                }}
              >
                Articles
              </button>
              <button
                onClick={() => navigate('/pricing')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: colors.textTertiary,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  padding: '0.5rem 1rem',
                }}
              >
                Pricing
              </button>
            </>
          )}
          {mobile && (
            <div style={{ position: 'relative' }} ref={dropdownRef}>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{
                  background: 'transparent',
                  border: '1px solid #475569',
                  color: '#94a3b8',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                <Menu size={18} />
                <ChevronDown size={14} />
              </button>
              {mobileMenuOpen && (
                <div 
                  style={{
                    position: 'absolute',
                    right: '0',
                    top: 'calc(100% + 0.5rem)',
                    background: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    minWidth: '140px',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                  }}>
                  <button
                    onClick={() => {
                      navigate('/articles');
                      setMobileMenuOpen(false);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: colors.primary,
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      padding: '0.75rem 1rem',
                      width: '100%',
                      textAlign: 'left',
                      fontWeight: '600',
                      borderBottom: '1px solid #334155'
                    }}
                  >
                    Articles
                  </button>
                  <button
                    onClick={() => {
                      navigate('/pricing');
                      setMobileMenuOpen(false);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#e2e8f0',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      padding: '0.75rem 1rem',
                      width: '100%',
                      textAlign: 'left'
                    }}
                  >
                    Pricing
                  </button>
                </div>
              )}
            </div>
          )}
          {!isAuthenticated ? (
            <>
              <button
                onClick={() => navigate('/login')}
                style={{
                  background: 'transparent',
                  border: '1px solid #475569',
                  color: colors.textPrimary,
                  fontSize: mobile ? '0.9rem' : '1rem',
                  fontWeight: '600',
                  padding: mobile ? '0.5rem 1rem' : '0.5rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/signup')}
                style={{
                  background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                  border: 'none',
                  color: 'white',
                  fontSize: mobile ? '0.9rem' : '1rem',
                  fontWeight: '600',
                  padding: mobile ? '0.5rem 1rem' : '0.5rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Sign Up Free
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/game')}
              style={{
                background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                border: 'none',
                color: 'white',
                fontSize: mobile ? '0.9rem' : '1rem',
                fontWeight: '600',
                padding: mobile ? '0.5rem 1rem' : '0.5rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Go to {isAuthenticated ? 'Dashboard' : 'Game'}
            </button>
          )}
        </div>
      </nav>
      
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '2rem'
      }}>
        {/* Page Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            background: 'rgba(96, 165, 250, 0.1)',
            borderRadius: '16px',
            marginBottom: '1.5rem'
          }}>
            <BookOpen size={48} color={colors.primary} />
          </div>
          <h1 style={{
            margin: '0 0 1rem 0',
            fontSize: '3rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Brain Training Articles
          </h1>
          <p style={{
            margin: 0,
            fontSize: '1.2rem',
            color: colors.textSecondary,
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            Evidence-based insights on cognitive training, working memory, 
            and maximizing your brain's potential
          </p>
        </div>

        {/* Featured Articles */}
        {featuredArticles.length > 0 && (
          <div style={{ marginBottom: '3rem' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: colors.textPrimary,
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{
                width: '6px',
                height: '24px',
                background: colors.primary,
                borderRadius: '3px'
              }}></span>
              Featured Articles
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1.5rem'
            }}>
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} featured={true} />
              ))}
            </div>
          </div>
        )}

        {/* Category Filter */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap',
          marginBottom: '2rem',
          justifyContent: 'center'
        }}>
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              style={{
                padding: '0.75rem 1.5rem',
                background: selectedCategory === category 
                  ? 'rgba(96, 165, 250, 0.2)' 
                  : 'rgba(30, 41, 59, 0.6)',
                border: selectedCategory === category
                  ? '2px solid rgba(96, 165, 250, 0.5)'
                  : '2px solid rgba(100, 116, 139, 0.3)',
                borderRadius: '12px',
                color: selectedCategory === category ? colors.primary : colors.textSecondary,
                fontWeight: selectedCategory === category ? '600' : '500',
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.2s',
                textTransform: 'capitalize'
              }}
            >
              {category}
            </button>
          ))}
        </div>

        {/* All Articles */}
        {error && (
          <div style={{
            padding: '1rem',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            color: '#fca5a5',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '1.5rem'
        }}>
          {filteredArticles.filter(a => !a.featured).map((article) => (
            <ArticleCard key={article.slug} article={article} />
          ))}
        </div>

        {filteredArticles.length === 0 && !loading && (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: colors.textSecondary
          }}>
            <p>No articles found in this category.</p>
          </div>
        )}

        {/* CTA Section */}
        <div style={{
          marginTop: '4rem',
          padding: '2rem',
          background: 'rgba(96, 165, 250, 0.1)',
          border: '1px solid rgba(96, 165, 250, 0.3)',
          borderRadius: '16px',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: colors.textPrimary,
            marginBottom: '1rem'
          }}>
            Ready to Start Training?
          </h3>
          <p style={{
            fontSize: '1.05rem',
            color: colors.textSecondary,
            marginBottom: '1.5rem',
            maxWidth: '600px',
            margin: '0 auto 1.5rem'
          }}>
            Apply what you've learned. Start your cognitive training journey today 
            with our advanced n-back trainer.
          </p>
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Link
              to="/game"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: colors.primary,
                color: colors.textPrimary,
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'transform 0.2s'
              }}
            >
              Start Training
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/pricing"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'rgba(30, 41, 59, 0.6)',
                border: '2px solid rgba(100, 116, 139, 0.3)',
                color: colors.textPrimary,
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'transform 0.2s'
              }}
            >
              View Plans
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const ArticleCard = ({ article, featured = false }) => {
  return (
    <Link
      to={`/articles/${article.slug}`}
      style={{
        display: 'block',
        background: featured 
          ? 'linear-gradient(135deg, rgba(96, 165, 250, 0.15) 0%, rgba(167, 139, 250, 0.15) 100%)'
          : 'rgba(30, 41, 59, 0.6)',
        border: featured
          ? '2px solid rgba(96, 165, 250, 0.4)'
          : '1px solid rgba(100, 116, 139, 0.3)',
        borderRadius: '16px',
        padding: '1.5rem',
        textDecoration: 'none',
        transition: 'all 0.3s',
        cursor: 'pointer'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.6)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = featured
          ? 'rgba(96, 165, 250, 0.4)'
          : 'rgba(100, 116, 139, 0.3)';
      }}
    >
      {/* Category & Read Time */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '1rem',
        flexWrap: 'wrap'
      }}>
        {article.category && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.25rem 0.75rem',
            background: 'rgba(167, 139, 250, 0.2)',
            border: '1px solid rgba(167, 139, 250, 0.3)',
            borderRadius: '6px',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: '#c4b5fd'
          }}>
            <Tag size={12} />
            {article.category}
          </div>
        )}
        {article.readTime && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.85rem',
            color: colors.textSecondary
          }}>
            <Clock size={14} />
            {article.readTime}
          </div>
        )}
      </div>

      {/* Title */}
      <h3 style={{
        margin: '0 0 0.75rem 0',
        fontSize: featured ? '1.35rem' : '1.2rem',
        fontWeight: '700',
        color: colors.textPrimary,
        lineHeight: '1.3'
      }}>
        {article.title}
      </h3>

      {/* Description */}
      <p style={{
        margin: '0 0 1rem 0',
        fontSize: '0.95rem',
        color: colors.textSecondary,
        lineHeight: '1.6'
      }}>
        {article.description}
      </p>

      {/* Read More Link */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        color: colors.primary,
        fontSize: '0.9rem',
        fontWeight: '600'
      }}>
        Read Article
        <ArrowRight size={16} />
      </div>
    </Link>
  );
};

export default ArticleListPage;
